import {Table, TableBody, TableCell, TableHead, TableHeader, TableRow} from "@/components/ui/table"
import useJobs from "@/query/workflowJob/useJobs"
import useJob from "@/query/workflowJob/useJob"
import {memo, useCallback, useMemo} from "react"
import VisibilitySensor from "@/components/ui/sensor"
import {useNavigate} from "react-router-dom"
import {formatIsoReadable} from "@/utils/dateUtils"
import type {Job} from "@/model/Job"
import {Button} from "@/components/ui/button"
import useWorkflows from "@/query/workflow/useWorkflows"
import JobStatusBadge from "@/view/workflowJob/components/JobStatusBadge"
import JobDetailSheet from "@/view/workflowJob/components/JobDetailSheet"
import {List} from "lucide-react"

export const WorkflowJobList = () => {
    const {data: jobsData, hasNextPage, fetchNextPage, isLoading} = useJobs()
    const {data: workflows} = useWorkflows()

    const workflowNameMap = useMemo(() => new Map<string, string>(
        (workflows?.pages ?? [])
            .flatMap((page) => page.content)
            .map((workflow) => [workflow._id, workflow.name ?? "-"] as [string, string])
    ), [workflows])

    const jobs = useMemo(() => {
        return jobsData?.pages.flatMap((page) => page.content) ?? []
    }, [jobsData])

    const handleScrollEnd = useCallback(async () => {
        if (hasNextPage && !isLoading) {
            await fetchNextPage()
        }
    }, [hasNextPage, isLoading, fetchNextPage])

    return (
        <div className="rounded-lg border bg-background overflow-hidden">
            <Table>
                <TableHeader>
                    <TableRow className="bg-muted/50 hover:bg-muted/50">
                        <TableHead className="w-[130px] text-center">Type</TableHead>
                        <TableHead className="text-center">Status</TableHead>
                        <TableHead className="text-center">Workflow</TableHead>
                        <TableHead className="text-center">Chatbot Name</TableHead>
                        <TableHead className="text-left">Created At</TableHead>
                        <TableHead className="w-[200px] text-left">Action</TableHead>
                    </TableRow>
                </TableHeader>
                <TableBody>
                    {jobs?.map((job) => (
                        <JobRow key={job._id} job={job} workflowNameMap={workflowNameMap}/>
                    ))}

                    {!isLoading && jobs?.length === 0 &&
                        <TableRow className="border-0">
                            <TableCell colSpan={6}
                                       className="text-muted-foreground text-sm py-8 text-center">
                                No Job result.
                            </TableCell>
                        </TableRow>
                    }

                    <TableRow className="border-0">
                        <TableCell colSpan={6}>
                            <VisibilitySensor onEnter={handleScrollEnd}/>
                        </TableCell>
                    </TableRow>
                </TableBody>
            </Table>
        </div>
    )
}

interface JobRowProps {
    job: Job
    workflowNameMap: Map<string, string>
}

const JobRow = memo<JobRowProps>(({job, workflowNameMap}) => {
    const navigate = useNavigate()
    const {data: runningJob} = useJob(job._id, job.status === "running")
    const displayJob = runningJob ?? job

    return (
        <TableRow>
            <TableCell className="font-medium text-center">
                {displayJob.type}
            </TableCell>
            <TableCell className="font-medium text-center">
                <JobStatusBadge job={displayJob}/>
            </TableCell>
            <TableCell className="font-medium text-center">
                {workflowNameMap.get(displayJob.workflowId) ?? "-"}
            </TableCell>
            <TableCell className="font-medium text-center">
                {displayJob.defaultParams?.chatbot_name ?? "-"}
            </TableCell>
            <TableCell className="font-medium text-left">
                {formatIsoReadable(displayJob.createdAt)}
            </TableCell>
            <TableCell className="w-[200px] text-left">
                <div className="flex items-center justify-start gap-2">
                    <JobDetailSheet job={displayJob}/>
                    <Button
                        type="button"
                        variant="outline"
                        size="sm"
                        className="gap-1.5"
                        onClick={() => navigate(`/workflow-service/jobs/${displayJob._id}`)}
                    >
                        <List className="h-4 w-4"/>
                        Results
                    </Button>
                </div>
            </TableCell>
        </TableRow>
    )
})

export default WorkflowJobList
